import { useState, useEffect } from 'react';
import { collection, getDocs, limit, query } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { useConversations } from '../hooks/useMessages';

const COLORS = ['#C94B30','#1D9E75','#534AB7','#BA7517','#085041','#3C3489','#D4537E','#378ADD'];
const getColor = s => COLORS[(s?.charCodeAt(0)||0) % COLORS.length];
const initials = n => (n||'U').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
const TAGS = ['#Afrobeats','#AfricanFashion','#Nollywood','#Safari','#AfricanFood','#Kente','#Jollof','#Ubuntu','#Amapiano','#Luganda'];
const REGIONS = ['All','West Africa','East Africa','Southern Africa','Central Africa','North Africa'];

export default function DiscoverPage() {
  const { user } = useAuth();
  const { createConversation } = useConversations();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [region, setRegion] = useState('All');
  const [messaging, setMessaging] = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const snap = await getDocs(query(collection(db, 'users'), limit(30)));
        setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => u.uid !== user?.uid));
      } finally { setLoading(false); }
    }
    load();
  }, [user]);

  async function handleMessage(u) {
    setMessaging(u.uid);
    try {
      await createConversation(u.uid, u.displayName);
      alert(`Chat with ${u.displayName} started! Go to Messages tab.`);
    } finally { setMessaging(null); }
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', flex:1, height:'100%', overflow:'hidden' }}>
      <div style={{ padding:'16px 20px 12px', borderBottom:'1px solid var(--border)', background:'var(--earth)', flexShrink:0 }}>
        <h2 style={{ fontFamily:'Sora,sans-serif', fontSize:18, fontWeight:600, color:'var(--text)' }}>Discover Africa</h2>
        <p style={{ fontSize:12, color:'var(--muted)', marginTop:2 }}>Find creators across the continent</p>
      </div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:6, padding:14, borderBottom:'1px solid var(--border)', flexShrink:0 }}>
        {TAGS.map(t => <div key={t} style={{ padding:'5px 14px', borderRadius:20, fontSize:12, background:'rgba(255,255,255,0.05)', border:'1px solid var(--border)', color:'var(--muted)', cursor:'pointer' }}>{t}</div>)}
      </div>
      <div style={{ display:'flex', gap:6, padding:'10px 14px', borderBottom:'1px solid var(--border)', overflowX:'auto', flexShrink:0 }}>
        {REGIONS.map(r => (
          <button key={r} onClick={() => setRegion(r)} style={{ padding:'5px 14px', borderRadius:20, fontSize:12, flexShrink:0, border:'1px solid', whiteSpace:'nowrap', borderColor: region===r ? 'var(--sunset)' : 'var(--border)', color: region===r ? 'var(--sunset)' : 'var(--muted)', background: region===r ? 'rgba(232,168,56,0.1)' : 'transparent', cursor:'pointer' }}>{r}</button>
        ))}
      </div>
      <div style={{ flex:1, overflowY:'auto', padding:14 }}>
        {loading && <div style={{ display:'flex', justifyContent:'center', padding:40 }}><div style={{ width:24, height:24, borderRadius:'50%', border:'2px solid var(--border)', borderTopColor:'var(--sunset)', animation:'spin 0.8s linear infinite' }} /></div>}
        {!loading && users.length === 0 && <div style={{ textAlign:'center', color:'var(--muted)', fontSize:14, marginTop:60 }}><div style={{ fontSize:36, marginBottom:10 }}>🌍</div>No creators yet — invite your friends!</div>}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px, 1fr))', gap:10 }}>
          {users.map(u => (
            <div key={u.uid} style={{ background:'var(--card)', border:'1px solid var(--border)', borderRadius:14, padding:'16px 12px', textAlign:'center', cursor:'pointer' }}>
              <div style={{ width:52, height:52, borderRadius:'50%', background:getColor(u.displayName), margin:'0 auto 10px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:600, color:'white', overflow:'hidden' }}>
                {u.photoURL ? <img src={u.photoURL} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : initials(u.displayName)}
              </div>
              <div style={{ fontSize:13, fontWeight:500, color:'var(--text)', marginBottom:2 }}>{u.displayName}</div>
              {u.country && <div style={{ fontSize:11, color:'var(--muted)', marginBottom:8 }}>📍 {u.country}</div>}
              <div style={{ display:'flex', gap:6, justifyContent:'center', marginBottom:8 }}>
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--sunset)' }}>{u.followers||0}</div>
                  <div style={{ fontSize:10, color:'var(--muted)' }}>followers</div>
                </div>
                <div style={{ width:1, background:'var(--border)' }} />
                <div style={{ textAlign:'center' }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--sunset)' }}>{u.posts||0}</div>
                  <div style={{ fontSize:10, color:'var(--muted)' }}>posts</div>
                </div>
              </div>
              <button onClick={() => handleMessage(u)} disabled={messaging===u.uid} style={{ width:'100%', padding:'6px 0', borderRadius:20, background:'transparent', border:'1px solid var(--border)', color:'var(--text)', fontSize:12, cursor:'pointer' }}>
                {messaging===u.uid ? '...' : '💬 Message'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
